import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const COUNTIES = ["New Castle", "Kent", "Sussex"];
const BUILT_IN = [["base_fee", "Service fee — flat"], ["same_address_fee", "2nd+ servee, same address"], ["rush_fee", "Rush add-on"], ["same_day_fee", "Same-day add-on"], ["included_pages", "Pages included"], ["page_fee", "Per page after that"], ["check_fee", "Check issuance"]];

const Field = ({ label, value, onChange, testid, dollar = true }) => (
  <div className="space-y-1">
    <Label className="sm-label">{label}</Label>
    <div className="relative">
      {dollar && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>}
      <Input className={`${dollar ? "pl-6" : ""} h-9 ${value !== undefined ? "border-sky-400 bg-sky-50" : ""}`} type="number" step="0.01" min="0" placeholder="leave as is" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" && value === undefined ? undefined : e.target.value)} data-testid={testid} />
    </div>
  </div>
);

/** Bulk rate editor: only fields you fill are applied to every selected company; everything else is left as-is. */
export function BulkRatesDialog({ ids, open, onClose, onDone }) {
  const [v, setV] = useState({});
  const [cf, setCf] = useState({});
  const [ov, setOv] = useState({});
  const [fees, setFees] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setV({}); setCf({}); setOv({}); api.get("/fees").then((r) => setFees(r.data.filter((f) => f.active))).catch(() => {}); } }, [open]);
  const touched = [...Object.values(v), ...Object.values(cf), ...Object.values(ov)].filter((x) => x !== undefined).length;
  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/clients/bulk-rates", { ids, rate_card: { ...v, county_fees: cf, fee_overrides: ov } });
      toast.success(`Updated ${data.fields} rate field${data.fields === 1 ? "" : "s"} on ${data.count} compan${data.count === 1 ? "y" : "ies"}`);
      onDone(); onClose();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : d ? JSON.stringify(d).slice(0, 200) : `Failed (${e?.response?.status || "network"})`, { duration: 8000 }); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bulk-rates-dialog">
        <DialogHeader><DialogTitle>Set rates for {ids.length} compan{ids.length === 1 ? "y" : "ies"}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-600">Only the boxes you type in are changed on every selected company — blank boxes leave that company's existing value alone. Type <b>0</b> to set a fee to zero.</div>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <div className="text-xs font-semibold text-slate-800 mb-2">Service fee by county</div>
            <div className="grid grid-cols-3 gap-3">{COUNTIES.map((c) => <Field key={c} label={`${c} County`} value={cf[c]} onChange={(x) => setCf({ ...cf, [c]: x })} testid={`bulk-county-${c.replace(" ", "-").toLowerCase()}`} />)}</div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-800 mb-2">Built-in fees</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{BUILT_IN.map(([k, l]) => <Field key={k} label={l} value={v[k]} onChange={(x) => setV({ ...v, [k]: x })} testid={`bulk-rate-${k}`} dollar={k !== "included_pages"} />)}</div>
          </div>
          {fees.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-800 mb-2">Additional fees <span className="font-normal text-slate-500">(default shown as placeholder)</span></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{fees.map((f) => <Field key={f.id} label={`${f.name}${f.unit === "per_unit" && f.unit_label ? ` / ${f.unit_label}` : ""}`} value={ov[f.id]} onChange={(x) => setOv({ ...ov, [f.id]: x })} testid={`bulk-fee-${f.id}`} />)}</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bulk-rates-cancel">Cancel</Button>
          <Button className="sm-green" onClick={apply} disabled={saving || !touched} data-testid="bulk-rates-apply">{saving ? "Applying…" : `Apply ${touched} field${touched === 1 ? "" : "s"}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
